import React, { useState } from 'react';
import { Calendar, MoreHorizontal, Edit2, Trash2, Eye } from 'lucide-react';
interface Project {
  id: number;
  title: string;
  description: string;
  status: string;
  dueDate: string;
  progress: number;
}
interface ProjectCardProps {
  project: Project;
  onViewDetails: (project: Project) => void;
  onEdit: (project: Project) => void;
  onDelete: (id: number) => void;
}
export function ProjectCard({
  project,
  onViewDetails,
  onEdit,
  onDelete
}: ProjectCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Terminé':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'En cours':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Planifié':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };
  const handleDelete = () => {
    onDelete(project.id);
    setShowDeleteConfirm(false);
  };
  return <div className="border rounded-lg overflow-hidden bg-card shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1" data-id="element-10">
      <div className="p-5" data-id="element-11">
        <div className="flex justify-between items-start mb-3" data-id="element-12">
          <h3 className="font-medium text-lg" data-id="element-13">{project.title}</h3>
          <div className="relative" data-id="element-14">
            <button onClick={() => setShowMenu(!showMenu)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-15">
              <MoreHorizontal size={18} data-id="element-16" />
            </button>
            {showMenu && <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border z-10" data-id="element-17">
                <button onClick={() => {
              onViewDetails(project);
              setShowMenu(false);
            }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" data-id="element-18">
                  <Eye size={16} data-id="element-19" />
                  Voir les détails
                </button>
                <button onClick={() => {
              onEdit(project);
              setShowMenu(false);
            }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" data-id="element-20">
                  <Edit2 size={16} data-id="element-21" />
                  Modifier
                </button>
                <button onClick={() => {
              setShowDeleteConfirm(true);
              setShowMenu(false);
            }} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-red-600" data-id="element-22">
                  <Trash2 size={16} data-id="element-23" />
                  Supprimer
                </button>
              </div>}
          </div>
        </div>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2" data-id="element-24">
          {project.description}
        </p>
        <div className="flex flex-wrap gap-2 mb-4" data-id="element-25">
          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(project.status)}`} data-id="element-26">
            {project.status}
          </span>
        </div>
        <div className="mb-4" data-id="element-27">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden" data-id="element-28">
            <div className="bg-blue-600 h-2 rounded-full transition-all duration-500" style={{
            width: `${project.progress}%`
          }} data-id="element-29"></div>
          </div>
          <div className="flex justify-between mt-1" data-id="element-30">
            <span className="text-xs text-muted-foreground" data-id="element-31">Progression</span>
            <span className="text-xs font-medium" data-id="element-32">{project.progress}%</span>
          </div>
        </div>
        <div className="flex items-center text-sm text-muted-foreground" data-id="element-33">
          <Calendar size={14} className="mr-1" data-id="element-34" />
          <span data-id="element-35">Échéance: {formatDate(project.dueDate)}</span>
        </div>
      </div>
      <div className="border-t px-5 py-3 bg-muted/30 flex justify-between" data-id="element-36">
        <button onClick={() => onViewDetails(project)} className="text-sm text-blue-600 hover:text-blue-800 transition-colors" data-id="element-37">
          Détails
        </button>
        <button onClick={() => onEdit(project)} className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-id="element-38">
          Modifier
        </button>
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDeleteConfirm(false)} data-id="element-39">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()} data-id="element-40">
            <h3 className="text-lg font-semibold mb-2" data-id="element-41">
              Confirmer la suppression
            </h3>
            <p className="text-muted-foreground mb-4" data-id="element-42">
              Êtes-vous sûr de vouloir supprimer le projet "{project.title}" ?
              Cette action est irréversible.
            </p>
            <div className="flex justify-end gap-3" data-id="element-43">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 text-sm border rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-44">
                Annuler
              </button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors" data-id="element-45">
                Supprimer
              </button>
            </div>
          </div>
        </div>}
    </div>;
}